/**
 * Starting a party order on the website.
 *
 * The app has had this for a while; the web had a button pointing at a page
 * that did not exist. Everything server-side was already built — this is the
 * organiser's half of it in a browser.
 *
 * Who owns a list, and how they get back to it:
 *
 *   Signed in  — the list is stored against the customer's email, so it
 *                follows the account. Any browser, any device, nothing to
 *                keep hold of.
 *   Signed out — the list is stored against an install id this browser makes
 *                and keeps. That works until they change device, so they are
 *                also given a link carrying the key, to save or mail to
 *                themselves. It is a capability: whoever holds it can manage
 *                the list, which is said plainly next to it.
 *
 * The guest link is a different thing entirely and always has been: it carries
 * the share token, and lets the people being sized vote and leave a size
 * without seeing anything of the organiser's account.
 */
(function () {
  'use strict';

  var KEY = 'tc_occasion_key';

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    // Old Safari. Same shape, same job.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function installId() {
    var fromUrl = new URLSearchParams(location.search).get('k');
    if (fromUrl && /^[0-9a-f-]{36}$/i.test(fromUrl)) {
      // Arrived on their own keep-link: adopt it as this browser's key.
      try { localStorage.setItem(KEY, fromUrl); } catch (e) { /* private mode */ }
      return fromUrl;
    }
    var stored = null;
    try { stored = localStorage.getItem(KEY); } catch (e) { /* private mode */ }
    if (stored) return stored;
    var made = uuid();
    try { localStorage.setItem(KEY, made); } catch (e) { /* private mode */ }
    return made;
  }

  function init(root) {
    var api = (root.getAttribute('data-endpoint') || '').replace(/\/$/, '');
    var guestBase = (root.getAttribute('data-guest-base') || '').replace(/\/$/, '');
    var email = root.getAttribute('data-email') || '';
    var form = root.querySelector('[data-tc-occ-form]');
    var out = root.querySelector('[data-tc-occ-out]');
    var listBox = root.querySelector('[data-tc-occ-lists]');
    if (!api || !form || !out) return;

    var key = installId();

    function note(kind, message) {
      out.innerHTML = '';
      var p = document.createElement('p');
      p.className = 'tc-note tc-note--' + kind;
      p.textContent = message;
      out.appendChild(p);
    }

    function linkRow(label, url, hint) {
      var wrap = document.createElement('div');
      wrap.className = 'tc-occ__link';
      var t = document.createElement('p');
      t.className = 'tc-occ__link-t';
      t.textContent = label;
      var row = document.createElement('div');
      row.className = 'tc-occ__link-row';
      var input = document.createElement('input');
      input.className = 'tc-field__in';
      input.readOnly = true;
      input.value = url;
      input.addEventListener('focus', function () { input.select(); });
      var copy = document.createElement('button');
      copy.type = 'button';
      copy.className = 'tc-pbtn tc-pbtn--ghost tc-occ__copy';
      copy.textContent = 'Copy';
      copy.addEventListener('click', function () {
        var done = function () { copy.textContent = 'Copied'; setTimeout(function () { copy.textContent = 'Copy'; }, 1800); };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(done, function () { input.select(); });
        } else {
          input.select();
          try { document.execCommand('copy'); done(); } catch (e) { /* let them copy by hand */ }
        }
      });
      row.appendChild(input);
      row.appendChild(copy);
      wrap.appendChild(t);
      wrap.appendChild(row);
      if (hint) {
        var h = document.createElement('p');
        h.className = 'tc-occ__hint';
        h.textContent = hint;
        wrap.appendChild(h);
      }
      return wrap;
    }

    function showList(list) {
      out.innerHTML = '';

      var head = document.createElement('div');
      head.className = 'tc-occ__made';
      var h = document.createElement('p');
      h.className = 'tc-occ__made-t';
      h.textContent = list.name;
      head.appendChild(h);
      if (list.eventDate) {
        var d = document.createElement('p');
        d.className = 'tc-occ__made-d';
        var parsed = new Date(list.eventDate + 'T00:00:00');
        d.textContent = isNaN(parsed.getTime()) ? list.eventDate
          : parsed.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        head.appendChild(d);
      }
      out.appendChild(head);

      out.appendChild(linkRow(
        'Send this to everyone being sized',
        guestBase + '/list/' + list.shareToken,
        'They vote on the style and leave their size. They see nothing of your account.'
      ));

      // Signed in, the account is the way back and there is nothing to keep.
      if (!email) {
        var keep = new URL(location.href);
        keep.search = '';
        keep.searchParams.set('k', key);
        out.appendChild(linkRow(
          'Your own way back to this list',
          keep.toString(),
          'Save this. It is how you reopen the list on another device — anyone with it can manage the list, so keep it to yourself. Signing in ties the list to your account instead.'
        ));
      }
    }

    function render(lists) {
      if (!listBox) return;
      listBox.innerHTML = '';
      if (!lists.length) return;
      var h = document.createElement('p');
      h.className = 'tc-occ__yours-h';
      h.textContent = lists.length === 1 ? 'Your list' : 'Your lists';
      listBox.appendChild(h);
      lists.forEach(function (list) {
        var row = document.createElement('div');
        row.className = 'tc-occ__yours';
        var name = document.createElement('span');
        name.className = 'tc-occ__yours-n';
        name.textContent = list.name;
        var meta = document.createElement('span');
        meta.className = 'tc-occ__yours-m';
        var bits = [];
        bits.push(list.members.length + (list.members.length === 1 ? ' person' : ' people'));
        if (list.outstanding) bits.push(list.outstanding + ' still to give a size');
        meta.textContent = bits.join(' · ');
        var open = document.createElement('a');
        open.className = 'tc-page__more';
        open.href = guestBase + '/list/' + list.shareToken;
        open.textContent = 'Open';
        row.appendChild(name);
        row.appendChild(meta);
        row.appendChild(open);
        listBox.appendChild(row);
      });
    }

    // Anything already going, on this browser or under this account.
    var url = api + '/lists?installId=' + encodeURIComponent(key) + (email ? '&email=' + encodeURIComponent(email) : '');
    fetch(url)
      .then(function (r) { return r.ok ? r.json() : []; })
      .then(function (lists) { render(Array.isArray(lists) ? lists : []); })
      .catch(function () { /* nothing to show is a fine outcome here */ });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var name = form.querySelector('[name="name"]').value.trim();
      var date = form.querySelector('[name="eventDate"]').value;
      var mail = email || (form.querySelector('[name="email"]') || {}).value || '';
      if (!name) { note('bad', 'Give the occasion a name — “Tom’s wedding” is plenty.'); return; }

      var button = form.querySelector('button[type="submit"]');
      var was = button.textContent;
      button.disabled = true;
      button.textContent = 'Starting…';

      fetch(api + '/lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ installId: key, name: name, eventDate: date || null, email: mail || null }),
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (list) {
          form.reset();
          showList(list);
          out.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        })
        .catch(function () {
          note('bad', 'That did not save. Try again in a moment, or ring the shop and we will set it up for you.');
        })
        .finally(function () {
          button.disabled = false;
          button.textContent = was;
        });
    });
  }

  function boot() {
    [].slice.call(document.querySelectorAll('[data-tc-occasion]')).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    [].slice.call(e.target.querySelectorAll('[data-tc-occasion]')).forEach(init);
  });
})();
